import {MODALS_IDS} from '@Components/modal-container';
import {openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {EventUpsellType, openEventsUpsellDialog} from '@Libraries/events.library';
import {openTicketingComingSoonModal} from '@Components/event-wizard/event-wizard-ticketing/ticketing-gating.helper';

// Keep in sync with App\Libraries\Facades\Event\EventTicketing\Enums\TicketingBlockedReason (backend is canonical).
export enum TicketingBlockedReason {
  PLAN = 'plan',
  COUNTRY = 'country',
  FEATURE = 'feature',
}

interface TicketingOnboardingLink {
  isAllowed: boolean;
  blockedReason: TicketingBlockedReason | null;
  needsOnboarding: boolean;
  // false = never submitted (initial CTA); true = submitted (isVerifying then splits verifying vs needs-more).
  detailsSubmitted: boolean;
  // true = details submitted and Stripe is verifying (nothing due, show "please wait"); false = action required.
  isVerifying: boolean;
  connectUrl: string | null;
}

/**
 * Asks the backend whether the organiser still needs Stripe onboarding and, if so, returns the
 * Stripe-hosted Account Link URL to send them to. Hi.Events owns the connected account and mints the
 * link; we just relay it.
 */
export const fetchTicketingOnboardingLink = async (): Promise<TicketingOnboardingLink> => {
  return (await window.PMW.writeLocal('eventticketing/getTicketingOnboardingLink')) as TicketingOnboardingLink;
};

/**
 * Hard onboarding gate for the step 2→3 transition on paid ticketing. The verdict is server-side
 * because window globals can be stale after an AJAX login. If the organiser is not allowed
 * (plan/country), opens the matching dialog and returns. If allowed and already onboarded, calls
 * onComplete immediately (advance). If allowed but still needs onboarding, opens the onboarding dialog
 * — passing onComplete so the dialog advances to Review itself once onboarding is confirmed (it mints a
 * fresh Stripe Account Link lazily on click, so the connectUrl in the response is unused here). Rejects
 * on a failed check so the caller can keep the user on step 2.
 */
export const runTicketingOnboardingGate = async (onComplete: () => void): Promise<void> => {
  const onboarding = await fetchTicketingOnboardingLink();
  if (!onboarding.isAllowed) {
    if (onboarding.blockedReason === TicketingBlockedReason.COUNTRY || onboarding.blockedReason === TicketingBlockedReason.FEATURE) {
      openTicketingComingSoonModal(onboarding.blockedReason === TicketingBlockedReason.COUNTRY);
    } else {
      openEventsUpsellDialog(EventUpsellType.TICKETING, undefined);
    }
    return;
  }
  if (!onboarding.needsOnboarding) {
    onComplete();
    return;
  }
  window.PMW.redux.store.dispatch(
    openPanelInModal({
      modalId: MODALS_IDS.TICKETING_STRIPE_ONBOARDING_MODAL,
      panelProps: {onComplete, detailsSubmitted: onboarding.detailsSubmitted, isVerifying: onboarding.isVerifying},
    })
  );
};
